import React, { useEffect, useMemo, useState } from "react"
import axios from "axios"
import { Modal, ModalHeader, ModalBody, ModalFooter, Badge } from "reactstrap"
import { getCurrentUser } from "../../helpers/Utils"
import { toast } from "react-toastify"

const authHeaders = () => ({
  "content-type": "application/json",
  Authorization: `Bearer ${
    getCurrentUser()?.token == null ? "" : getCurrentUser().token
  }`,
})

const companyBaseUrl = () =>
  `${process.env.REACT_APP_API_URL}/${localStorage.getItem("company_name")}`

const normalizeWaNumber = (raw, countryCode) => {
  let digits = String(raw || "").replace(/\D+/g, "")
  if (!digits) return ""

  const cc = String(countryCode || "91").replace(/\D+/g, "") || "91"

  // Already international — don't double-prefix country code
  if (digits.length >= 11 && digits.length <= 15) {
    if (cc && digits.startsWith(cc)) return digits
    return digits
  }

  if (digits.length === 10) {
    // Drop leading 0 if somehow present on longer national numbers elsewhere
    return `${cc}${digits}`
  }

  // National with leading 0 (e.g. 09876543210)
  if (digits.length === 11 && digits.startsWith("0")) {
    return `${cc}${digits.slice(1)}`
  }

  return digits
}

const detectBodyVarCount = components => {
  const body = (components || []).find(
    c => String(c.type || "").toUpperCase() === "BODY"
  )
  if (!body?.text) return 0
  const matches = String(body.text).match(/\{\{(\d+)\}\}/g) || []
  return new Set(matches.map(m => m.replace(/[{}]/g, ""))).size
}

const detectHeaderMediaType = components => {
  const header = (components || []).find(
    c => String(c.type || "").toUpperCase() === "HEADER"
  )
  const format = String(header?.format || "").toUpperCase()
  if (["IMAGE", "VIDEO", "DOCUMENT"].includes(format)) {
    return format.toLowerCase()
  }
  return null
}

const MetaWhatsAppSendModal = ({
  isOpen,
  toggle,
  leadName,
  leadNumber,
  countryCode,
}) => {
  const [sending, setSending] = useState(false)
  const [connection, setConnection] = useState(null)
  const [to, setTo] = useState("")
  const [templates, setTemplates] = useState([])
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [selectedKey, setSelectedKey] = useState("")
  const [headerLink, setHeaderLink] = useState("")
  const [bodyParams, setBodyParams] = useState([])

  const selected = useMemo(() => {
    if (!selectedKey) return null
    return templates.find(t => `${t.name}||${t.language}` === selectedKey) || null
  }, [selectedKey, templates])

  const headerMediaType = detectHeaderMediaType(selected?.components)
  const bodyVarCount = detectBodyVarCount(selected?.components)

  useEffect(() => {
    if (!isOpen) return
    setTo(normalizeWaNumber(leadNumber, countryCode))
    setSelectedKey("")
    setHeaderLink("")
    setBodyParams([])
    setTemplates([])

    axios
      .get(`${companyBaseUrl()}/whatsapp/meta/status`, {
        headers: authHeaders(),
      })
      .then(res => {
        if (res.data.status && res.data.data?.connected) {
          setConnection(res.data.data.connection)
        } else {
          setConnection(null)
        }
      })
      .catch(() => setConnection(null))

    setLoadingTemplates(true)
    axios
      .get(`${companyBaseUrl()}/whatsapp/meta/templates`, {
        headers: authHeaders(),
        params: { status: "APPROVED" },
      })
      .then(res => {
        setLoadingTemplates(false)
        if (res.data.status) {
          const rows = res.data.data?.templates || []
          setTemplates(Array.isArray(rows) ? rows : [])
          if (rows.length > 0) {
            const key = `${rows[0].name}||${rows[0].language}`
            setSelectedKey(key)
            const count = detectBodyVarCount(rows[0].components)
            setBodyParams(Array(count).fill(""))
          }
        }
      })
      .catch(() => {
        setLoadingTemplates(false)
        setTemplates([])
      })
  }, [isOpen, leadNumber, countryCode])

  const onSelectTemplate = key => {
    setSelectedKey(key)
    setHeaderLink("")
    const tpl = templates.find(t => `${t.name}||${t.language}` === key)
    const count = detectBodyVarCount(tpl?.components)
    setBodyParams(Array(count).fill(""))
  }

  const send = () => {
    if (!to) {
      toast.error("Recipient number is required.", {
        position: "top-center",
        autoClose: 4000,
      })
      return
    }
    if (!selected) {
      toast.error("Select an approved template.", {
        position: "top-center",
        autoClose: 5000,
      })
      return
    }
    if (headerMediaType && !headerLink.trim()) {
      toast.error(
        `This template needs a ${headerMediaType} URL in the header.`,
        { position: "top-center", autoClose: 5000 }
      )
      return
    }
    if (bodyVarCount > 0) {
      const missing = bodyParams.some(v => !String(v || "").trim())
      if (missing) {
        toast.error("Fill all body variable values.", {
          position: "top-center",
          autoClose: 5000,
        })
        return
      }
    }

    const payload = {
      to,
      type: "template",
      template_name: selected.name,
      language_code: selected.language || "en_US",
    }
    if (headerMediaType && headerLink.trim()) {
      payload.header_type = headerMediaType
      payload.header_link = headerLink.trim()
    }
    if (bodyVarCount > 0) {
      payload.body_params = bodyParams.map(v => String(v).trim())
    }

    setSending(true)
    axios
      .post(`${companyBaseUrl()}/whatsapp/meta/send-message`, payload, {
        headers: authHeaders(),
      })
      .then(res => {
        setSending(false)
        if (res.data.status) {
          toast.success(res.data.message || "Message sent successfully.", {
            position: "top-center",
            autoClose: 5000,
          })
          toggle()
        } else {
          toast.error(res.data.message || "Unable to send message.", {
            position: "top-center",
            autoClose: 7000,
          })
        }
      })
      .catch(err => {
        setSending(false)
        toast.error(
          err?.response?.data?.message || "Unable to send message.",
          { position: "top-center", autoClose: 10000 }
        )
      })
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle}>Send WhatsApp message</ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <div className="text-muted small text-uppercase mb-1">Customer</div>
          <div className="fw-semibold">{leadName || "—"}</div>
        </div>

        <div className="mb-3">
          <div className="text-muted small text-uppercase mb-1">
            Business number
          </div>
          {connection ? (
            <div className="d-flex align-items-center gap-2">
              <Badge color="success" pill>
                Connected
              </Badge>
              <span className="fw-semibold">
                {connection.display_phone_number || "—"}
              </span>
            </div>
          ) : (
            <span className="text-danger">
              WhatsApp Business is not connected for this workspace.
            </span>
          )}
        </div>

        <div className="mb-3">
          <label className="form-label">Recipient</label>
          <input
            type="text"
            className="form-control"
            value={to}
            onChange={e => setTo(e.target.value)}
            placeholder="9198xxxxxxxx"
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Template</label>
          {loadingTemplates ? (
            <p className="small text-muted mb-0">Loading templates…</p>
          ) : templates.length > 0 ? (
            <select
              className="form-select"
              value={selectedKey}
              onChange={e => onSelectTemplate(e.target.value)}
            >
              {templates.map(t => (
                <option
                  key={`${t.id}-${t.name}-${t.language}`}
                  value={`${t.name}||${t.language}`}
                >
                  {t.name} ({t.language})
                </option>
              ))}
            </select>
          ) : (
            <small className="text-warning">
              No approved templates found. Create or sync templates first.
            </small>
          )}
        </div>

        {headerMediaType ? (
          <div className="mb-3">
            <label className="form-label">
              Header {headerMediaType} URL *
            </label>
            <input
              type="url"
              className="form-control"
              value={headerLink}
              onChange={e => setHeaderLink(e.target.value)}
              placeholder={`https://… (${headerMediaType})`}
            />
            <small className="text-muted">
              Public HTTPS link sent as the template header attachment.
            </small>
          </div>
        ) : null}

        {bodyVarCount > 0
          ? bodyParams.map((val, i) => (
              <div className="mb-3" key={i}>
                <label className="form-label">
                  Variable {"{{"}
                  {i + 1}
                  {"}}"} *
                </label>
                <input
                  className="form-control"
                  value={val}
                  onChange={e => {
                    const next = [...bodyParams]
                    next[i] = e.target.value
                    setBodyParams(next)
                  }}
                  placeholder={`Value for {{${i + 1}}}`}
                />
              </div>
            ))
          : null}
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn-light" onClick={toggle}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-success"
          onClick={send}
          disabled={sending || !connection || !selected}
        >
          {sending ? "Sending…" : "Send message"}
        </button>
      </ModalFooter>
    </Modal>
  )
}

export default MetaWhatsAppSendModal
